import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Figure, Flow } from '../components/Diagram';

interface Axis {
  key: string;
  name: string;
  blurb: string;
}

const AXES: Axis[] = [
  { key: 'PRICE', name: 'Price', blurb: 'Override any monetary or quantity field — flat unit price, the tier ladder, stairstep fees, the overage price, included units, or committed quantity. One axis covers the whole price shape.' },
  { key: 'MODEL', name: 'Pricing model', blurb: 'Change the pricing model entirely for one customer (e.g. flat rate → commission).' },
  { key: 'PRORATION', name: 'Proration', blurb: 'Swap the proration calculation — Full month / Daily / None — for this customer.' },
  { key: 'BILLING_TIMING', name: 'Billing timing', blurb: 'Switch Advance ↔ Arrears for this customer only.' },
  { key: 'CURRENCY', name: 'Currency', blurb: 'Bill this customer in a different currency than the component default.' },
];

const LIFECYCLE = [
  {
    state: 'Draft',
    body:
      'You author the agreement, pick the override axes you care about, and fill in the new values. Drafts are editable indefinitely and never affect billing.',
  },
  {
    state: 'Published',
    body:
      'Publishing freezes the agreement and signals intent. From this point it is immutable — to change a value you must Revoke and re-author. Publishing also slots the agreement into the resolution chain.',
  },
  {
    state: 'Effective',
    body:
      'A Published agreement takes effect at the customer\'s next renewal on or after effectiveFrom. The renewal invoice uses the overridden numbers; the underlying components on other customers are unaffected.',
  },
  {
    state: 'Revoked / expired',
    body:
      'Revoke at any time to remove the override at the next renewal. If expiresAt is set, a background sweep moves the agreement to Archived after that date and the customer renews back onto the base component pricing.',
  },
];

export function CustomAgreementsGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Custom Pricing Agreements — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Custom pricing agreements</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Negotiate a <em>private</em> deal.
        </h1>
        <p className="dp-oneliner">
          A Custom Pricing Agreement overrides one Variable Component for one customer. It beats
          every Variable Component Price scope, takes effect at the customer&rsquo;s next renewal,
          and is fully audited via the Lifecycle Event log.
        </p>
      </header>

      <section className="dp-section">
        <h2>When to write one</h2>
        <p className="dp-plain">
          Reach for a Custom Pricing Agreement when:
        </p>
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>You&rsquo;ve negotiated bespoke unit pricing with a single customer.</li>
          <li>You need to override more than one axis at once (e.g. <code>PRICE</code> + <code>CURRENCY</code>).</li>
          <li>You want a different pricing <em>model</em> for one customer — say, switching them from flat to commission.</li>
          <li>You want an audited, sweepable, time-bounded override rather than a quiet edit at the Subscription price scope.</li>
        </ul>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          For one-off rate tweaks limited to a single subscription, a Subscription-scope
          <Link to="/docs/concepts/variableComponentPrice"> Variable Component Price</Link> may
          be enough. The Agreement is the right tool when more than one axis moves, or when
          finance needs the change recorded as an explicit deal.
        </p>
      </section>

      <section className="dp-section">
        <h2>Override axes</h2>
        <p className="dp-section-sub">
          You opt into specific axes via the <code>overrideScope</code> array. Only the axes you
          opt into are required — everything else falls through to the base component.
        </p>
        <div className="dp-fields-table">
          {AXES.map((a) => (
            <div className="dp-field-row" key={a.key}>
              <div className="dp-field-k">{a.name}</div>
              <div className="dp-field-v">{a.blurb}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>Lifecycle</h2>
        <p className="dp-section-sub">
          Draft → Published → Effective → (Revoked or Expired).
        </p>
        <ol className="qs-steps">
          {LIFECYCLE.map((l, i) => (
            <li className="qs-step" key={l.state}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>{i + 1}</span>
              <div>
                <div className="qs-step-label">{l.state}</div>
                <div className="qs-step-detail">{l.body}</div>
              </div>
            </li>
          ))}
        </ol>
        <Figure
          title="Life of an agreement"
          caption="Only a Published agreement can bill; changing a value means revoking and re-authoring."
        >
          <Flow
            steps={[
              { title: 'Draft', text: 'Editable, never affects billing.', tone: 'muted' },
              { title: 'Published', text: 'Frozen and added to the resolution chain.' },
              { title: 'Effective', text: 'Applies at the next renewal on or after effectiveFrom.', tone: 'accent' },
              { fork: [
                { title: 'Revoked', text: 'Override removed at the next renewal.', tone: 'danger' },
                { title: 'Expired', text: 'Archived after expiresAt; base pricing returns.', tone: 'muted' },
              ] },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <h2>How it wins</h2>
        <p className="dp-plain">
          Custom Pricing Agreements override everything below them. The full resolution order at
          billing time, from highest to lowest priority, is:
        </p>
        <pre className="api-code" style={{ marginTop: 18 }}>{`Custom Pricing Agreement (Published, effective)
        ↓
Subscription-scope Variable Component Price
        ↓
Bundle-scope Variable Component Price
        ↓
Module-scope Variable Component Price
        ↓
Global default Variable Component Price`}</pre>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The agreement is matched on <strong>(customer, component)</strong> — not on the
          subscription. If a customer holds multiple subscriptions and they all include the same
          Variable Component, a single agreement applies to all of them at their respective next
          renewals.
        </p>
      </section>

      <section className="dp-section">
        <h2>Audit trail</h2>
        <p className="dp-plain">
          Every publish, revoke, and expiry sweep is captured by the
          <Link to="/docs/concepts/lifecycleEvent"> Lifecycle Event log</Link>. When the
          agreement takes effect at renewal, the renewal&rsquo;s RENEWED event already carries
          the new MRR delta — there is no separate &ldquo;agreement applied&rdquo; event,
          because the agreement is just one input to the standard renewal pricing.
        </p>
      </section>

      <section className="dp-section">
        <h2>Common patterns</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Discount one component</div>
            <div className="dp-field-v">Override <code>PRICE</code> for the seats component (its unit price), leave everything else inherited.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Enterprise rate card</div>
            <div className="dp-field-v">Override <code>PRICE</code> with the customer-specific tier ladder agreed in the contract.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Local-currency billing</div>
            <div className="dp-field-v">Override <code>CURRENCY</code> + <code>PRICE</code> together so the customer is billed in EUR at a fixed rate.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Take-rate deal</div>
            <div className="dp-field-v">Override <code>MODEL</code> to switch a flat-rate component to a commission component for one marketplace customer.</div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Next up</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/pricing-models" style={{ color: 'inherit', textDecoration: 'none' }}>
              Pricing models reference ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/scoped-pricing" style={{ color: 'inherit', textDecoration: 'none' }}>
              Scope-based pricing ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/versioning" style={{ color: 'inherit', textDecoration: 'none' }}>
              Versioning &amp; grandfathering ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/override-pricing" style={{ color: 'inherit', textDecoration: 'none' }}>
              Overriding base &amp; component pricing directly ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/billing-timing" style={{ color: 'inherit', textDecoration: 'none' }}>
              Advance vs. arrears billing ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
